import React from 'react';

import CTextInput from '../../components/Forms/CTextInput';
import CDateInput from '../../components/Forms/CDateInput';
import CAssetInput from '../../components/Forms/CAssetInput';
import CNumberInput from '../../components/Forms/CNumberInput';
import CBooleanInput from '../../components/Forms/CBooleanInput';
import CTextListInput from '../../components/Forms/CTextListInput';
import CDateListInput from '../../components/Forms/CDateListInput';
import CDropDownInput from '../../components/Forms/CDropDownInput';
import CAssetListInput from '../../components/Forms/CAssetListInput';
import CNumberListInput from '../../components/Forms/CNumberListInput';
import CDropDownListInput from '../../components/Forms/CDropDownListInput';
import CXYTCredentialInput from '../../components/Forms/CXYTCredentialInput';
import { TextInput } from '../../store/inputs/TextInput';
import { DateInput } from '../../store/inputs/DateInput';
import { AssetInput } from '../../store/inputs/AssetInput';
import COAuthCredentialInput from '../../components/Forms/COAuthCredentialInput';
import { NumberInput } from '../../store/inputs/NumberInput';
import { BooleanInput } from '../../store/inputs/BooleanInput';
import { DateListInput } from '../../store/inputs/DateListInput';
import { TextListInput } from '../../store/inputs/TextListInput';
import { AssetListInput } from '../../store/inputs/AssetListInput';
import { NumberListInput } from '../../store/inputs/NumberListInput';
import { InputTypes, NumberInputType, InputClass } from '../../store/inputs/types';
import { XYTCredentialInput } from '../../store/inputs/XYTCredentialInput';
import { OAuthCredentialInput } from '../../store/inputs/OAuthCredentialInput';
import { DataTypeList } from '../../types/DataTypeList';


const tagInputSwitch = (f: InputTypes): InputTypes | null => {
  switch (f.tag) {
    case 'openIDSub':
    case 'openIDToken':
      return new OAuthCredentialInput(f);
    case 'xytMinutiae':
      return new XYTCredentialInput(f);
    default:
      return null;
  }
};

const ListTypesInputSwitch = (f: InputTypes): InputTypes | null => {
  switch (f.dataType) {
    case '[]string':
    case '[]url':
      return new TextListInput(f);
    case '[]number':
      return new NumberListInput(f);
    case '[]datetime':
      return new DateListInput(f);
    default:
      break;
  }

  if (f.dataType.indexOf('[]') >= 0) {
    if ((f as NumberInputType).dropDownValues) {
      return new NumberListInput({ ...f, dataType: '[]number' });
    }
    return new AssetListInput(f);
  }

  return null;
};

const dataTypeInputSwitch = (f: InputTypes, dtList: DataTypeList): InputTypes => {
  const defaultOnChange = (v: any) => { };
  const defaultNumberOnChange = (v: string | number | null | undefined) => { };
  const onChange = f.dataType === 'number' || (f as NumberInputType).dropDownValues ? defaultNumberOnChange : defaultOnChange;
  const clearValue = () => { };
  const toggleDisabled = () => { };
  f = { ...f, onChange, clearValue, toggleDisabled } as InputClass;
  switch (f.dataType) {
    case 'boolean':
      return new BooleanInput(f);
    case 'string':
    case 'url':
      return new TextInput({ ...f, onChange, clearValue, toggleDisabled });
    case 'number':
      return new NumberInput({ ...f, onChange, clearValue, toggleDisabled });
    case 'datetime':
      return new DateInput({ ...f, onChange, clearValue, toggleDisabled });
    default:
      if (f.dataType.indexOf('->') >= 0) {
        f.dataType = f.dataType.replace('->', '');
        return new AssetInput(f);
      }

      if ((f as NumberInputType).dropDownValues) {
        return new NumberInput({ ...f, dataType: 'number', onChange, clearValue, toggleDisabled });
      }

      const dtype = dtList.find(d => d.dataType === f.dataType)
      if (dtype && dtype.acceptedFormats && dtype.acceptedFormats === 'string') {
        return new TextInput({ ...f, dataType: 'string', onChange, clearValue, toggleDisabled });
      } else if (dtype && dtype.acceptedFormats && dtype.acceptedFormats === 'number') {
        return new NumberInput({ ...f, dataType: 'number', onChange, clearValue, toggleDisabled });
      }

      return new TextInput({ ...f, dataType: 'string', onChange, clearValue, toggleDisabled });
  }
};

export const fromJsonToFormContext = (f: InputTypes, dtList: DataTypeList): InputTypes => {
  const tagInput = tagInputSwitch(f);

  if (tagInput) return tagInput;

  const listInput = ListTypesInputSwitch(f);

  if (listInput) return listInput;

  return dataTypeInputSwitch(f, dtList);
};

// Transforms inputs in components
export const getForm = (item: InputTypes, keyId: string): JSX.Element => {
  // eslint-disable-next-line no-param-reassign
  item.isEdit = (keyId && keyId !== '') as boolean;

  switch (item.tag) {
    case 'openIDSub':
    case 'openIDToken':
      return (
        <COAuthCredentialInput
          key={item.tag}
          item={item as OAuthCredentialInput}
        />
      );
    case 'xytMinutiae':
      return (
        <CXYTCredentialInput key={item.tag} item={item as XYTCredentialInput} />
      );
    default:
      break;
  }

  switch (item.dataType) {
    case '[]string':
    case '[]url':
      return <CTextListInput key={item.tag} item={item as TextListInput} />;
    case '[]number':
      if ((item as NumberInput).dropDownValues) {
        return (
          <CDropDownListInput key={item.tag} item={item as NumberListInput} />
        );
      }
      return <CNumberListInput key={item.tag} item={item as NumberListInput} />;
    case '[]datetime':
      return <CDateListInput key={item.tag} item={item as DateListInput} />;
    default:
      break;
  }

  if (item.dataType.indexOf('->') >= 0 || item.dataType.indexOf('-%3E') >= 0) {
    // eslint-disable-next-line no-param-reassign
    item.dataType = item.dataType.replace('->', '').replace('-%3E', '');
  }

  if (item.dataType.indexOf('[]') >= 0) {
    if ((item as NumberInput).dropDownValues) {
      return (
        <CDropDownListInput key={item.tag} item={item as NumberListInput} />
      );
    }
    return <CAssetListInput key={item.tag} item={item as AssetListInput} />;
  }

  switch (item.dataType) {
    case 'boolean':
      return <CBooleanInput key={item.tag} item={item as BooleanInput} />;
    case 'string':
    case 'url':
      return <CTextInput key={item.tag} item={item as TextInput} />;
    case 'number':
      if ((item as NumberInput).dropDownValues) {
        return <CDropDownInput key={item.tag} item={item as NumberInput} />;
      }
      return <CNumberInput key={item.tag} item={item as NumberInput} />;
    case 'datetime':
      return <CDateInput key={item.tag} item={item as DateInput} />;
    default:
      return <CAssetInput key={item.tag} item={item as AssetInput} />;
  }
};
